import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { BokkzClient, ClientEntity, IntermediaryEntity } from '../services/api.bokkz.services';
import { FormBuilder } from '@angular/forms';
import { ToastService } from '@snelstart/ui-toast';
import { SelectFormControlItem, SharedUiFormControlsModule } from '@snelstart/ui-form-controls';
import { EMPTY, filter, firstValueFrom, Subject, take, takeUntil, tap } from 'rxjs';
import { SharedUiButtonModule } from '@snelstart/ui-button';
import { SharedUiDetailsModule } from '@snelstart/ui-details';
import { SharedUiDialogModule } from '@snelstart/ui-dialog';
import { SharedUiTabModule } from '@snelstart/ui-tab';
import { SharedUiAlertModule } from '@snelstart/ui-alert';
import { CommonModule } from '@angular/common';
import {
    AddressModel,
    CustomerClient,
    CustomerQueryClient,
    CustomerRequest,
    CustomerResponse
} from '../services/rekenclient.api';
import { BokkzAlgLogic } from '../services/bokkz-alg-logic';
import { BokkzAlgDataService } from '../services/bokkz-alg-data-service';
import { switchMap } from 'rxjs/operators';

interface TaxItem {
    taxId?: string;
    taxIdIncomeTax?: string;
    taxIdVat?: string;
    taxIdWageTax?: string;
    taxIdCorporateTax?: string;
    valueAddedTax?: boolean;
    postponement?: boolean;
    corporateTax?: boolean;
    incomeTax?: boolean;
    wageTax?: boolean;
    kvkId?: string;
    classificatie?: string;
    sbiCode?: string;
    dossiers?: boolean;
}

type TaxKey =
    | 'valueAddedTax'
    | 'incomeTax'
    | 'corporateTax'
    | 'wageTax'
    | 'dossiers'
    | 'bezwaar'
    | 'salaris'
    | 'dividend'
    | 'gifttax'
    | 'digipoort';

@Component({
    selector: 'snelstart-venture-fiscaal-domain-logic-client-migration',
    templateUrl: './client-migration.component.html',
    imports: [
        SharedUiButtonModule,
        SharedUiFormControlsModule,
        SharedUiDetailsModule,
        SharedUiDialogModule,
        SharedUiTabModule,
        SharedUiAlertModule,
        CommonModule
    ],
    styleUrl: './client-migration.component.css',
    standalone: true
})
export class ClientMigrationComponent implements OnInit, OnDestroy {
    @Input() public dialogVisible = false;
    @Output() public dialogVisibleChange = new EventEmitter<boolean>();

    private destroy$ = new Subject<void>();
    public loading = false;
    public migratingClients = false;
    public error?: any;
    public clientsMigrated = 0;
    public clientsToMigrate = 0;
    progressPercent = 0;
    public clientId = {} as number;
    public selectedTab = 0;
    public providerId = {} as string;
    public focusedElementId: string | null = null;
    public index = 0;
    public intermediary = {} as IntermediaryEntity;

    public optionsJaNee: SelectFormControlItem<boolean>[] = [
        { value: true, label: 'Ja' },
        { value: false, label: 'Nee' }
    ];

    constructor(
        public toastService: ToastService,
        private customerQueryClient: CustomerQueryClient,
        private bokkzClient: BokkzClient,
        private customerClient: CustomerClient,
        private algLogic: BokkzAlgLogic,
        public algDataService: BokkzAlgDataService,
        public fb: FormBuilder
    ) {
    }

    ngOnInit() {
        this.algDataService.intermediary
            .pipe(
                take(1),
                tap(intermediary => {
                    this.intermediary = intermediary;
                }),
                switchMap(intermediary => {
                    if (!intermediary?.sstPrimaryAdminId) {
                        this.dialogVisibleChange.emit(false);
                        return EMPTY;
                    }
                    return this.algLogic.verrijkToken(intermediary.sstPrimaryAdminId);
                }), take(1),
                filter((token) => {
                    if (!token) {
                        console.error('Token could not be fetched');
                        return false;
                    }
                    return true;
                }),
                switchMap(() => this.bokkzClient.getClientsMigrated()),
                takeUntil(this.destroy$)
            )
            .subscribe((clientListMigration) => {
                if (clientListMigration.length > 0) {
                    this.migratingClients = true;
                    this.clientsToMigrate = clientListMigration.length;
                    if (clientListMigration.length > 0) {
                        const value = this.migrateClients(clientListMigration);
                        console.log(value)
                    }
                }
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private async migrateClients(clients: ClientEntity[]): Promise<void> {
        this.clientsMigrated = 0;
        const aggregatedErrors: Array<{
            error: unknown;
            client: ClientEntity;
        }> = [];
        for (const client of clients) {
            if (!client.sstPrimaryAdminId) {
                client.sstPrimaryAdminId = this.intermediary.sstPrimaryAdminId;
            }
            if (!client.sstRelationId) {
                // ✅ Check if ALL tax IDs are missing
                try {
                    // ✅ Create customer in relation API

                    const customerRequest = await this.mapClientToCustomerRequest(client);
                    const customerResponse: CustomerResponse = await this.createCustomerWithRetry(customerRequest);

                    if (!customerResponse?.id) {
                        console.warn(`❌ Customer creation failed for client ${client.achternaam}: no ID returned.`);
                        continue;
                    }

                    // ✅ Enrich client with new relation ID
                    client.sstRelationId = customerResponse.id;

                    // ✅ Save back to bokkz client system
                    await firstValueFrom(this.bokkzClient.postClient(client));
                    console.log(`✅ Client ${client.id} succesvol gemigreerd → relationId: ${customerResponse.id}`);
                } catch (error) {
                    aggregatedErrors.push({
                        error,
                        client
                    });
                    console.error(`🚨 Migration failed for client ${client.id}:${client.achternaam}:`, error);
                }

                this.clientsMigrated++;
                this.progressPercent = Math.round((this.clientsMigrated / this.clientsToMigrate) * 100);
                // 🕒 Optional: delay a bit to avoid hitting rate limits
                await new Promise((res) => setTimeout(res, 500));
            }
        }
        if (aggregatedErrors.length > 0) {
            this.bokkzClient.sendError({
                message: 'Client migration errors occurred',
                count: aggregatedErrors.length,
                errors: aggregatedErrors
            }).subscribe(() => {
                console.log(`🚨 Aggregated error email sent (${aggregatedErrors.length} errors)`);
            });
        }
        console.log(`🎉 Migratie voltooid: ${this.clientsMigrated}/${clients.length}`);
    }

    private async mapClientToCustomerRequest(client: ClientEntity): Promise<CustomerRequest> {
        const customer = new CustomerRequest();
        const defaultName = 'Onbekende klant';
        customer.name =
            client.name?.trim().slice(0, 50) ||
            `${client.voorletters ?? ''} ${client.tussenvoegsel ?? ''} ${client.achternaam ?? ''}`.trim().slice(0, 50) ||
            defaultName;
        let code: number | undefined;
        while (code === undefined) {
            try {
                code = await firstValueFrom(this.customerQueryClient.getNextCustomerCode());
            } catch (err) {
                console.error('Failed to get next customer code, retrying...', err);
            }
        }
        customer.isActive = !client.deleted;
        customer.memo = client.classificatie;
        customer.kvk = client.kvkId;

        customer.vat = this.verifyVat(client.taxIdVat);
        customer.phoneNumber = client.phone;
        customer.mobileNumber = client.mobile;
        customer.website = client.website;
        customer.emailAddressLine = client.email ? [client.email] : [];
        customer.memo = 'klant accountancyportaal';

        const address = new AddressModel();
        address.streetName = client.street || undefined;
        address.streetNumber = Number(client.houseNumber?.toString().trim()) || undefined;
        address.affix = client.numberAddition || undefined;
        address.postalCode = this.formatPostalCode(client.postalCode) || undefined;
        address.city = client.place || undefined;
        if (address.streetName?.trim() && address.streetNumber?.toString().trim() && address.postalCode?.trim() && address.city?.trim()) {
            customer.address = address;
        }
        customer.correspondenceContactName = client.roepnaam || customer.name;
        customer.paymentPreference = undefined;
        customer.customerGroupId = undefined;

        return customer;
    }

    private async createCustomerWithRetry(customerRequest: CustomerRequest, retries = 5): Promise<CustomerResponse> {
        for (let attempt = 1; attempt <= retries; attempt++) {
            try {
                return await firstValueFrom(this.customerClient.createCustomer(customerRequest));
            } catch (err: any) {
                const status = err?.status;
                const message = err?.error?.message ?? '';
                const waitTime = parseInt(message.match(/\d+/)?.[0] ?? '5', 10) * 1000; // default to 5 seconds

                if (status === 429 && attempt < retries) {
                    console.warn(`⚠️ [${attempt}/${retries}] Rate limit exceeded. Retrying in ${waitTime / 1000} seconds...`);
                    await new Promise((resolve) => setTimeout(resolve, waitTime));
                } else {
                    // ❌ If not rate limit OR final retry, throw the error
                    console.error(`❌ CreateCustomer failed on attempt ${attempt}:`, err);
                    throw err;
                }
            }
        }

        throw new Error(`Failed to create customer after ${retries} retries due to rate limiting.`);
    }

    verifyVat(taxIdVat: string | undefined): string | undefined {
        if (!taxIdVat) return undefined;

        // Remove spaces and uppercase
        let vat = taxIdVat.toUpperCase().replace(/\s+/g, '');

        // Step 1: Ensure prefix (two letters)
        let prefixMatch = /^[A-Z]{2}/.exec(vat);
        let prefix = prefixMatch ? prefixMatch[0] : 'NL';
        let rest = prefixMatch ? vat.slice(2) : vat;

        // Step 2: Ensure suffix (B + 2 digits)
        let suffixMatch = /B\d{2}$/.exec(rest);
        let suffix = suffixMatch ? suffixMatch[0] : 'B01';
        let core = suffixMatch ? rest.slice(0, -3) : rest;

        // Step 3: Validate elfproef on 9 digits core
        if (!/^\d{9}$/.test(core)) {
            // Core is not 9 digits → invalid
            return undefined;
        }

        const digits = core.split('').map((d) => parseInt(d, 10));
        const sum = digits.reduce((acc, val, idx) => acc + (idx + 1) * val, 0);
        if (sum % 11 !== 0) {
            // Elfproef failed → invalid
            return undefined;
        }

        // Step 4: Rebuild VAT
        return `${prefix}${core}${suffix}`;
    }

    formatPostalCode(postalCode: string | undefined): string | undefined {
        if (!postalCode) return undefined;

        const cleaned = postalCode.replace(/\s+/g, '').toUpperCase();

        const match = cleaned.match(/^(\d{4})([A-Z]{2})$/);
        if (!match) return undefined;

        return `${match[1]} ${match[2]}`;
    }
}
